import { useState } from 'react';

import { BookModal } from '@/features/library/components/BookModal.js';
import { pickColors } from '@/features/library/components/bookcase/spine-utils.js';
import { useFeaturedBook } from '@/features/library/hooks/useFeaturedBook.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';
import { SafeCoverImg } from '@/shared/components/SafeCoverImg.js';

/**
 * "Pépite de la semaine" — tirée déterministiquement chaque lundi (ISO week)
 * dans la pool de livres rating ≥ 4.3, ≥ 10 avis, sortis dans les 24 derniers
 * mois (avec repli plus laxiste si la pool est vide).
 */
export function FeaturedBook(): JSX.Element | null {
  const { data, isLoading } = useFeaturedBook();
  const { data: shelfData } = useMyShelf();
  const [open, setOpen] = useState(false);

  if (isLoading) {
    return (
      <section className="featured-card">
        <p className="text-sm text-brown-soft">Sélection en cours…</p>
      </section>
    );
  }

  const book = data?.book ?? null;
  if (!book) return null;

  const [c1, c2] = pickColors(book);
  const entry =
    shelfData?.items.find((e) => e.book.id === book.id) ?? null;

  return (
    <section className="featured-card">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="featured-cover relative cursor-pointer"
        style={
          {
            ['--c1' as string]: c1,
            ['--c2' as string]: c2,
          } as React.CSSProperties
        }
        aria-label={`Ouvrir ${book.title}`}
      >
        <SafeCoverImg
          url={book.coverUrl}
          className="absolute inset-0 h-full w-full object-cover"
          fallback={
            <span className="px-2 text-center font-serif text-base leading-tight text-cream-light">
              {book.title}
            </span>
          }
        />
      </button>
      <div className="featured-body">
        <p className="font-script text-base text-terracotta-dark">
          ✨ La pépite de la semaine
        </p>
        <h2 className="font-serif text-2xl font-semibold leading-tight text-brown md:text-3xl">
          {book.title}
        </h2>
        <p className="mt-1 text-sm text-brown-soft">
          {book.author}
          {book.year ? ` · ${book.year}` : ''}
        </p>
        {book.rating !== null && (
          <p className="mt-1 text-xs text-brown-light">
            <span className="text-gold" aria-hidden>
              ★
            </span>{' '}
            <span className="font-semibold text-brown">
              {book.rating.toFixed(1)}
            </span>
            {book.ratingsCount !== null &&
              ` · ${book.ratingsCount.toLocaleString('fr-FR')} avis`}
          </p>
        )}
        {book.synopsis && (
          <p className="mt-3 line-clamp-4 text-sm leading-relaxed text-brown-soft">
            {book.synopsis}
          </p>
        )}
        <div className="mt-auto flex flex-wrap gap-2 pt-4">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-full bg-terracotta px-4 py-2 text-xs font-semibold text-cream-light hover:bg-terracotta-dark"
          >
            ＋ Ajouter
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-full bg-paper px-4 py-2 text-xs font-semibold text-brown ring-1 ring-cream-dark hover:bg-cream-dark"
          >
            Voir le livre
          </button>
        </div>
      </div>

      {open && (
        <BookModal book={book} entry={entry} onClose={() => setOpen(false)} />
      )}
    </section>
  );
}
